<template>
  <div class="custom-select-container relative w-full min-w-[200px]" ref="selectContainer">
    <select
      :name="name"
      v-model="selectedValue"
      class="native-select"
      style="display: none"
    >
      <option v-if="placeholder" value="">{{ placeholder }}</option>
      <option
        v-for="option in options"
        :key="option.value"
        :value="option.value"
      >
        {{ option.label }}
      </option>
    </select>

    <div
      class="custom-select-trigger"
      @click="toggle"
      :class="{ open: isOpen }"
    >
      {{ selectedOption ? selectedOption.label : placeholder }}
    </div>

    <div class="custom-select-options" :class="{ open: isOpen }">
      <div
        v-for="option in options"
        :key="option.value"
        class="custom-select-option"
        :class="{ selected: option.value === selectedValue }"
        @click="selectOption(option)"
      >
        {{ option.label }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from "vue";

const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: "",
  },
  options: {
    type: Array,
    required: true,
    // Each option should have { value: string|number, label: string }
  },
  placeholder: {
    type: String,
    default: "",
  },
  name: {
    type: String,
    default: "select-option",
  },
});

const emit = defineEmits(["update:modelValue", "change"]);

const selectContainer = ref(null);
const isOpen = ref(false);
const selectedValue = ref(props.modelValue);

const selectedOption = computed(() => {
  return props.options.find((option) => option.value === selectedValue.value);
});

// Watch for external value changes
watch(
  () => props.modelValue,
  (newValue) => {
    selectedValue.value = newValue;
  }
);

// Watch for internal value changes
watch(selectedValue, (newValue) => {
  emit("update:modelValue", newValue);
});

const toggle = () => {
  isOpen.value = !isOpen.value;
};

const close = () => {
  isOpen.value = false;
};

const selectOption = (option) => {
  selectedValue.value = option.value;
  emit("change", {
    value: option.value,
    text: option.label,
  });
  close();
};

const handleClickOutside = (event) => {
  if (selectContainer.value && !selectContainer.value.contains(event.target)) {
    close();
  }
};

const handleKeyboard = (e) => {
  if (!isOpen.value && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
    e.preventDefault();
    isOpen.value = true;
    return;
  }

  const currentIndex = props.options.findIndex(
    (opt) => opt.value === selectedValue.value
  );
  let nextIndex;

  switch (e.key) {
    case "ArrowDown":
      e.preventDefault();
      nextIndex =
        currentIndex < props.options.length - 1 ? currentIndex + 1 : 0;
      selectOption(props.options[nextIndex]);
      break;

    case "ArrowUp":
      e.preventDefault();
      nextIndex =
        currentIndex > 0 ? currentIndex - 1 : props.options.length - 1;
      selectOption(props.options[nextIndex]);
      break;

    case "Enter":
    case "Space":
      e.preventDefault();
      if (isOpen.value) {
        close();
      } else {
        isOpen.value = true;
      }
      break;

    case "Escape":
      close();
      break;
  }
};

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
  document.addEventListener("keydown", handleKeyboard);
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
  document.removeEventListener("keydown", handleKeyboard);
});
</script>

<style scoped>
.custom-select-trigger {
  -webkit-tap-highlight-color: transparent;
  background-color: #fff;
  border-radius: 8px;
  border: solid 1px #35363b;
  box-sizing: border-box;
  color: #35363b;
  clear: both;
  cursor: pointer;
  display: block;
  letter-spacing: 0.32px;
  font-size: 14px;
  font-family: "Reddit Sans", "serif";
  font-weight: normal;
  height: 44px;
  display: flex;
  align-items: center;
  outline: none;
  padding-left: 18px;
  padding-right: 30px;
  position: relative;
  text-align: left !important;
  -webkit-transition: all 0.2s ease-in-out;
  transition: all 0.2s ease-in-out;
  user-select: none;
  white-space: nowrap;
  @media (min-width: 768px) {
    font-size: 16px;
  }
}

.custom-select-trigger::after {
  content: "\f107";
  display: block;
  pointer-events: none;
  position: absolute;
  -webkit-transition: all 0.15s ease-in-out;
  transition: all 0.15s ease-in-out;
  width: 5px;
  font-weight: bold;
  font-family: "Font Awesome 6 Free";
  left: auto;
  width: 40px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  right: 0;
  top: 0;
}
.custom-select-trigger::before {
  width: 40px;
  height: 100%;
  position: absolute;
  right: 0;
  top: 0;
  background-color: #ecede9;
  content: "";
  border-radius: 0 8px 8px 0;
  border-left: solid 1px #35363b;
}
.custom-select-trigger.open::after {
  transform: rotate(-180deg);
  top: 0px;
}

.custom-select-options {
  background-color: #fff;
  border-radius: 5px;
  box-shadow: 0 0 0 1px rgba(68, 68, 68, 0.11);
  box-sizing: border-box;
  opacity: 0;
  overflow: hidden;
  padding: 0;
  pointer-events: none;
  position: absolute;
  top: 97%;
  left: 0;
  -webkit-transform-origin: 50% 0;
  -ms-transform-origin: 50% 0;
  transform-origin: 50% 0;
  -webkit-transform: scale(0.75) translateY(-21px);
  -ms-transform: scale(0.75) translateY(-21px);
  transform: scale(0.75) translateY(-21px);
  -webkit-transition:
    all 0.2s cubic-bezier(0.5, 0, 0, 1.25),
    opacity 0.15s ease-out;
  transition:
    all 0.2s cubic-bezier(0.5, 0, 0, 1.25),
    opacity 0.15s ease-out;
  z-index: 9;
  @media (min-width: 768px) {
    font-size: 16px;
  }
}

.custom-select-options.open {
  opacity: 1;
  width: 100%;
  pointer-events: auto;
  -webkit-transform: scale(1) translateY(0);
  -ms-transform: scale(1) translateY(0);
  transform: scale(1) translateY(0);
}

.custom-select-option {
  cursor: pointer;
  font-weight: 400;
  line-height: 40px;
  list-style: none;
  min-height: 40px;
  outline: none;
  padding-left: 18px;
  padding-right: 29px;
  text-align: left;
  -webkit-transition: all 0.2s;
  transition: all 0.2s;
  font-size: 14px;
  @media (min-width: 768px) {
    font-size: 16px;
  }
}

.custom-select-option.selected {
  font-weight: bold;
}
</style>
